// Map display options + filters. Desktop: floating panel; mobile: bottom
// sheet. Friendly controls compile to GraphQL filters / SQL function args in
// lib/filters.ts — no raw filter JSON in the UI.
import { X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ACTIVITIES, ACTIVITY_ICONS, PLACE_TYPES, PLACE_TYPE_ICONS } from "../lib/format";
import { countActiveFilters, EMPTY_FILTERS, type FilterState } from "../lib/filters";
import { useT } from "../lib/i18n";
import { Chip, Field, TextInput } from "./ui";

type Props = {
  filters: FilterState;
  onChange: (next: FilterState) => void;
  onClose: () => void;
};

export function FilterSheet({ filters, onChange, onClose }: Props) {
  const { t, labelizeT } = useT();
  const toggle = <K extends "activities" | "placeTypes">(key: K, value: FilterState[K][number]) => {
    const list = filters[key] as string[];
    onChange({
      ...filters,
      [key]: list.includes(value) ? list.filter((v) => v !== value) : [...list, value],
    });
  };

  return (
    <div className="fixed inset-0 z-40 sm:absolute sm:inset-auto sm:right-3 sm:top-14 sm:w-96">
      {/* Backdrop, mobile only */}
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px] sm:hidden" onClick={onClose} />
      <div className="absolute inset-x-0 bottom-0 max-h-[85%] overflow-y-auto rounded-t-2xl bg-card text-card-foreground p-5 pb-[calc(1.25rem+env(safe-area-inset-bottom))] shadow-pop sm:static sm:max-h-[70vh] sm:rounded-xl sm:border">
        {/* Drag affordance, mobile sheet only */}
        <div className="mx-auto mb-4 h-1 w-10 rounded-full bg-muted sm:hidden" />
        <div className="mb-4 flex items-center justify-between">
          <h2 className="text-base font-semibold tracking-tight text-foreground">{t("filters.title")}</h2>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label={t("filters.closeAria")}
            className="text-muted-foreground hover:text-foreground"
          >
            <X aria-hidden className="size-4" />
          </Button>
        </div>

        <section className="mb-4">
          <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("filters.showOnMap")}</h3>
          <div className="flex flex-wrap gap-2">
            <Chip active={filters.showOutings} onClick={() => onChange({ ...filters, showOutings: !filters.showOutings })}>
              {t("filters.outings")}
            </Chip>
            {/* Routes + tracks form one visual group: tracks are a route
                sub-layer, so the tracks chip only exists while routes are on
                (showTracks itself is preserved for when they come back). */}
            <div className="flex shrink-0 items-center gap-1.5 rounded-full border border-dashed p-1 pl-1.5">
              <Chip active={filters.showRoutes} onClick={() => onChange({ ...filters, showRoutes: !filters.showRoutes })}>
                {t("filters.routes")}
              </Chip>
              {filters.showRoutes && (
                <Chip active={filters.showTracks} onClick={() => onChange({ ...filters, showTracks: !filters.showTracks })}>
                  {t("filters.routeTracks")}
                </Chip>
              )}
            </div>
            <Chip active={filters.showRegions} onClick={() => onChange({ ...filters, showRegions: !filters.showRegions })}>
              {t("filters.regions")}
            </Chip>
            <Chip active={filters.showEvents} onClick={() => onChange({ ...filters, showEvents: !filters.showEvents })}>
              {t("filters.events")}
            </Chip>
          </div>
          {filters.showRoutes && filters.showTracks && (
            <p className="mt-2 text-xs text-muted-foreground">{t("filters.tracksHint")}</p>
          )}
          {/* Display option, not a layer — its own row below the layer chips. */}
          <div className="mt-2.5 flex flex-wrap gap-2">
            <Chip active={filters.clustering} onClick={() => onChange({ ...filters, clustering: !filters.clustering })}>
              {t("filters.clustering")}
            </Chip>
          </div>
        </section>

        <section className="mb-4">
          <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("filters.routeActivities")}</h3>
          <div className="flex flex-wrap gap-2">
            {ACTIVITIES.map((a) => {
              const Icon = ACTIVITY_ICONS[a];
              return (
                <Chip key={a} active={filters.activities.includes(a)} onClick={() => toggle("activities", a)}>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon aria-hidden className="size-3.5" />
                    {labelizeT(a)}
                  </span>
                </Chip>
              );
            })}
          </div>
        </section>

        <section className="mb-4">
          <h3 className="mb-2.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("filters.placeTypes")}</h3>
          <div className="flex flex-wrap gap-2">
            {PLACE_TYPES.map((pt) => {
              const Icon = PLACE_TYPE_ICONS[pt];
              return (
                <Chip key={pt} active={filters.placeTypes.includes(pt)} onClick={() => toggle("placeTypes", pt)}>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon aria-hidden className="size-3.5" />
                    {labelizeT(pt)}
                  </span>
                </Chip>
              );
            })}
          </div>
        </section>

        <section className="mb-4 grid grid-cols-2 gap-3">
          <Field label={t("filters.elevMin")}>
            <TextInput
              type="number"
              inputMode="numeric"
              value={filters.elevMin ?? ""}
              onChange={(e) => onChange({ ...filters, elevMin: e.target.value ? Number(e.target.value) : undefined })}
            />
          </Field>
          <Field label={t("filters.elevMax")}>
            <TextInput
              type="number"
              inputMode="numeric"
              value={filters.elevMax ?? ""}
              onChange={(e) => onChange({ ...filters, elevMax: e.target.value ? Number(e.target.value) : undefined })}
            />
          </Field>
          <Field label={t("filters.outingsFrom")}>
            <TextInput
              type="date"
              value={filters.dateFrom ?? ""}
              onChange={(e) => onChange({ ...filters, dateFrom: e.target.value || undefined })}
            />
          </Field>
          <Field label={t("filters.outingsUntil")}>
            <TextInput
              type="date"
              value={filters.dateTo ?? ""}
              onChange={(e) => onChange({ ...filters, dateTo: e.target.value || undefined })}
            />
          </Field>
        </section>

        <section className="mb-4 grid grid-cols-2 gap-3">
          <Field label={t("filters.distMin")}>
            <TextInput
              type="number"
              inputMode="numeric"
              value={filters.distMinKm ?? ""}
              onChange={(e) => onChange({ ...filters, distMinKm: e.target.value ? Number(e.target.value) : undefined })}
            />
          </Field>
          <Field label={t("filters.distMax")}>
            <TextInput
              type="number"
              inputMode="numeric"
              value={filters.distMaxKm ?? ""}
              onChange={(e) => onChange({ ...filters, distMaxKm: e.target.value ? Number(e.target.value) : undefined })}
            />
          </Field>
          <Field label={t("filters.ascentMin")}>
            <TextInput
              type="number"
              inputMode="numeric"
              value={filters.ascentMin ?? ""}
              onChange={(e) => onChange({ ...filters, ascentMin: e.target.value ? Number(e.target.value) : undefined })}
            />
          </Field>
        </section>

        <section className="mb-4">
          <Field label={t("filters.nameContains")}>
            <TextInput
              value={filters.q ?? ""}
              placeholder="e.g. Sněžka"
              onChange={(e) => onChange({ ...filters, q: e.target.value || undefined })}
            />
          </Field>
        </section>

        {countActiveFilters(filters) > 0 && (
          <Button
            type="button"
            variant="outline"
            onClick={() =>
              onChange({
                ...EMPTY_FILTERS,
                showRoutes: filters.showRoutes,
                showTracks: filters.showTracks,
                clustering: filters.clustering,
                showRegions: filters.showRegions,
                showOutings: filters.showOutings,
                showEvents: filters.showEvents,
              })
            }
            className="w-full"
          >
            {t("filters.clear")}
          </Button>
        )}
      </div>
    </div>
  );
}
